import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { ArrowLeft, ExternalLink, AlertTriangle } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';
import { useSession } from '../auth/SessionContext.jsx';

// Defensive default-icon fix (we use divIcon, but other plugins might pull defaults).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const FRANCE_CENTER = [46.5, 2.5];
const FRANCE_ZOOM = 6;
const REFRESH_MS = 60_000;

const STATUT_LABEL = {
  normal: 'Normal',
  attention: 'Attention',
  critique: 'Critique',
  inconnu: 'Inconnu',
};

const STATUT_COLOR = {
  normal: '#2EB6A7',
  attention: '#f97316',
  critique: '#c0392b',
  inconnu: '#a8b4c9',
};

const STATUT_BG = {
  normal: '#e6f7f4',
  attention: '#fff1e6',
  critique: '#fdecea',
  inconnu: '#eef0f4',
};

/* ---------- helpers ---------- */

function timeAgo(ms) {
  if (!ms) return 'jamais';
  const diff = Date.now() - ms;
  if (diff < 60_000) return "à l'instant";
  if (diff < 3600_000) return `il y a ${Math.floor(diff / 60000)} min`;
  if (diff < 86400_000) return `il y a ${Math.floor(diff / 3600000)} h`;
  return `il y a ${Math.floor(diff / 86400000)} j`;
}

function formatXAxis(ms, rangeMs) {
  if (!ms) return '';
  const d = new Date(ms);
  if (rangeMs > 86_400_000) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}`;
  }
  const hh = String(d.getHours()).padStart(2, '0');
  const mn = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mn}`;
}

/* ---------- icons ---------- */

function makeDot(color, alert = false) {
  return L.divIcon({
    className: 'vrt-sensor-marker',
    html: `<span class="vrt-sensor-dot${alert ? ' vrt-sensor-dot--alert' : ''}" style="background:${color}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -10],
  });
}

const ICONS = {
  normal: makeDot(STATUT_COLOR.normal),
  attention: makeDot(STATUT_COLOR.attention),
  critique: makeDot(STATUT_COLOR.critique),
  inconnu: makeDot(STATUT_COLOR.inconnu),
  alert: makeDot(STATUT_COLOR.critique, true),
};

function pickIcon(item) {
  if (item.eau_detectee === true) return ICONS.alert;
  return ICONS[item.statut || 'inconnu'] || ICONS.inconnu;
}

/* ---------- FitToBounds ---------- */

function FitToBounds({ items }) {
  const map = useMap();
  const didFit = useRef(false);
  useEffect(() => {
    if (didFit.current || !items.length) return;
    const valid = items.filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude));
    if (!valid.length) return;
    const bounds = L.latLngBounds(valid.map((s) => [s.latitude, s.longitude]));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
      didFit.current = true;
    }
  }, [items, map]);
  return null;
}

/* ---------- main page ---------- */

export default function AdminGestionMap() {
  const { session } = useSession();
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  if (session && session.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  /* fetch sensors */
  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const list = await api.get('/api/admin/sensors');
      const arr = Array.isArray(list?.items) ? list.items : [];
      setItems(arr);
    } catch (e) {
      setError(e.message || 'Erreur de chargement des capteurs');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll(false);
  }, []);

  /* auto-refresh every 60 seconds (silent) */
  useEffect(() => {
    const id = setInterval(() => {
      fetchAll(true);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  /* filter: only sensors linked to an avaloir AND with valid coords */
  const validItems = useMemo(() => {
    return items.filter(
      (s) =>
        s.avaloir_id != null &&
        Number.isFinite(s.latitude) &&
        Number.isFinite(s.longitude)
    );
  }, [items]);

  return (
    <div className="vrt-sensor-map-page">
      <Header />

      <main className="vrt-sensor-map-main">
        {/* Sticky header bar */}
        <div className="vrt-sensor-map-bar">
          <button
            type="button"
            className="vrt-sensor-map-back"
            onClick={() => nav('/admin/gestion')}
            title="Retour à la liste"
          >
            <ArrowLeft size={16} /> Retour à la liste
          </button>
          <div className="vrt-sensor-map-count">
            {loading
              ? 'Chargement…'
              : `${validItems.length} capteur${validItems.length > 1 ? 's' : ''} sur la carte`}
          </div>
        </div>

        {/* Legend */}
        <div className="vrt-sensor-map-legend">
          <span className="vrt-sensor-map-legend-item">
            <span className="vrt-sensor-map-legend-dot" style={{ background: STATUT_COLOR.normal }} />
            Normal (&lt;50%)
          </span>
          <span className="vrt-sensor-map-legend-item">
            <span className="vrt-sensor-map-legend-dot" style={{ background: STATUT_COLOR.attention }} />
            Attention (50-80%)
          </span>
          <span className="vrt-sensor-map-legend-item">
            <span className="vrt-sensor-map-legend-dot" style={{ background: STATUT_COLOR.critique }} />
            Critique (&gt;80%)
          </span>
          <span className="vrt-sensor-map-legend-item">
            <span
              className="vrt-sensor-map-legend-dot vrt-sensor-map-legend-dot--alert"
              style={{ background: STATUT_COLOR.critique }}
            />
            Eau détectée
          </span>
        </div>

        {error && (
          <div className="vrt-sensor-map-error" role="alert">
            Erreur : {error}
          </div>
        )}

        <div className="vrt-sensor-map-wrap">
          {!loading && validItems.length === 0 ? (
            <div className="vrt-sensor-map-empty">
              <p>
                Aucun capteur lié à un avaloir avec coordonnées.
                <br />
                Lie d&apos;abord un capteur depuis la page Gestion.
              </p>
              <button
                type="button"
                className="vrt-btn vrt-btn--teal"
                onClick={() => nav('/admin/gestion')}
                style={{ minHeight: 40, padding: '0 16px' }}
              >
                <ArrowLeft size={16} /> Retour à la liste
              </button>
            </div>
          ) : (
            <MapContainer
              center={FRANCE_CENTER}
              zoom={FRANCE_ZOOM}
              scrollWheelZoom
              style={{ width: '100%', height: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <FitToBounds items={validItems} />

              {validItems.map((s) => (
                <Marker
                  key={s.device_id}
                  position={[s.latitude, s.longitude]}
                  icon={pickIcon(s)}
                >
                  <Popup maxWidth={340} minWidth={300}>
                    <SensorPopup item={s} onOpenDetail={() => nav(`/admin/gestion?device=${encodeURIComponent(s.device_id)}`)} />
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

          {loading && (
            <div className="vrt-sensor-map-loading" aria-live="polite">
              Chargement…
            </div>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        .vrt-sensor-map-page { min-height: 100vh; background: #fafbfc; display: flex; flex-direction: column; }
        .vrt-sensor-map-main {
          flex: 1;
          max-width: 1400px; width: 100%;
          margin: 0 auto; padding: 16px 16px 40px;
          display: flex; flex-direction: column; gap: 12px;
        }

        .vrt-sensor-map-bar {
          position: sticky; top: 0;
          z-index: 5;
          display: flex; justify-content: space-between; align-items: center;
          gap: 12px; flex-wrap: wrap;
          padding: 10px 14px;
          background: #fff;
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          box-shadow: 0 1px 4px rgba(26, 40, 73, 0.05);
        }
        .vrt-sensor-map-back {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px;
          border: 1px solid var(--vrt-border); border-radius: 6px;
          background: #fff; color: var(--vrt-navy);
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: all 0.15s;
        }
        .vrt-sensor-map-back:hover { background: var(--vrt-bg-alt); }
        .vrt-sensor-map-count {
          font-size: 13px; font-weight: 600; color: var(--vrt-navy);
        }

        .vrt-sensor-map-legend {
          display: flex; flex-wrap: wrap; gap: 14px;
          align-items: center;
          padding: 10px 14px;
          background: rgba(227, 231, 238, 0.3);
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          font-size: 12px;
          color: var(--vrt-navy);
        }
        .vrt-sensor-map-legend-item {
          display: inline-flex; align-items: center; gap: 6px;
        }
        .vrt-sensor-map-legend-dot {
          display: inline-block;
          width: 12px; height: 12px;
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 0 0 1px var(--vrt-border);
        }
        .vrt-sensor-map-legend-dot--alert {
          animation: vrt-sensor-pulse 1.4s ease-in-out infinite;
        }

        .vrt-sensor-map-error {
          background: #fdecea; color: #a23123;
          padding: 10px 14px; border-radius: 8px;
          font-size: 13px;
        }

        .vrt-sensor-map-wrap {
          position: relative;
          width: 100%;
          height: calc(100dvh - 240px);
          min-height: 420px;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid var(--vrt-border);
          background: #fff;
        }
        .vrt-sensor-map-wrap .leaflet-container { width: 100% !important; height: 100% !important; }

        .vrt-sensor-map-empty {
          height: 100%;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          text-align: center; gap: 16px;
          padding: 24px;
          color: var(--vrt-text-muted);
          font-size: 14px;
          line-height: 1.5;
        }

        .vrt-sensor-map-loading {
          position: absolute; top: 16px; left: 50%;
          transform: translateX(-50%);
          background: rgba(26, 40, 73, 0.92);
          color: #fff;
          padding: 6px 14px;
          border-radius: 999px;
          font-size: 13px;
          z-index: 1001;
        }

        /* sensor markers */
        .vrt-sensor-dot {
          display: block;
          width: 18px; height: 18px;
          border-radius: 50%;
          border: 3px solid #fff;
          box-shadow: 0 1px 4px rgba(0,0,0,.4);
        }
        .vrt-sensor-dot--alert {
          animation: vrt-sensor-pulse 1.4s ease-in-out infinite;
        }
        @keyframes vrt-sensor-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(192,57,43,0.45), 0 1px 4px rgba(0,0,0,.4); }
          50% { box-shadow: 0 0 0 10px rgba(192,57,43,0), 0 1px 4px rgba(0,0,0,.4); }
        }

        .leaflet-popup-content { margin: 12px 14px; font-family: var(--vrt-font); }
        .leaflet-popup-content-wrapper { border-radius: 12px; }

        @media (max-width: 720px) {
          .vrt-sensor-map-main { padding: 10px 8px 32px; }
          .vrt-sensor-map-wrap { height: calc(100dvh - 280px); min-height: 360px; }
          .vrt-sensor-map-legend { gap: 10px; font-size: 11px; }
        }
      `}</style>
    </div>
  );
}

/* ---------- popup (with on-demand history chart) ---------- */

function SensorPopup({ item, onOpenDetail }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const statut = item.statut || 'inconnu';
  const color = STATUT_COLOR[statut];
  const isAlert = item.eau_detectee === true;
  const taux = Math.max(0, Math.min(100, item.taux_remplissage ?? 0));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setHistory(null);
    (async () => {
      try {
        const data = await api.get(
          `/api/admin/sensors/${encodeURIComponent(item.device_id)}/history?limit=100`
        );
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
        // Sort oldest -> newest for the chart
        const sorted = [...arr].sort((a, b) => (a.measured_at || 0) - (b.measured_at || 0));
        setHistory(sorted);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Erreur de chargement de l'historique");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.device_id]);

  /* prepare chart data */
  const { chartData, rangeMs, waterPoints } = useMemo(() => {
    if (!history || !history.length) return { chartData: [], rangeMs: 0, waterPoints: [] };
    const first = history[0].measured_at || 0;
    const last = history[history.length - 1].measured_at || 0;
    const rng = Math.max(0, last - first);
    const data = history.map((h, i) => ({
      idx: i,
      ts: h.measured_at,
      label: formatXAxis(h.measured_at, rng),
      taux: h.distance_mm === -1 ? 100 : (h.taux ?? null),
      raw_distance: h.distance_mm,
      is_water: h.distance_mm === -1,
    }));
    const water = data.filter((d) => d.is_water);
    return { chartData: data, rangeMs: rng, waterPoints: water };
  }, [history]);

  return (
    <div style={{ width: 300, maxWidth: '100%' }}>
      {isAlert && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: '#c0392b',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            marginBottom: 8,
            letterSpacing: 0.04,
          }}
        >
          <AlertTriangle size={12} />
          EAU DÉTECTÉE
        </div>
      )}

      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: 'var(--vrt-navy)',
          fontFamily: 'Menlo, Consolas, monospace',
          wordBreak: 'break-all',
          marginBottom: 2,
          lineHeight: 1.2,
        }}
      >
        {item.device_id}
      </div>
      <div style={{ fontSize: 12, color: 'var(--vrt-text-muted)', marginBottom: 8 }}>
        {item.secteur || '— Secteur non renseigné'}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{taux}%</div>
        <span
          style={{
            background: STATUT_BG[statut],
            color,
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.04,
          }}
        >
          {STATUT_LABEL[statut]}
        </span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--vrt-navy)', marginBottom: 4 }}>
        Distance :{' '}
        <strong>
          {item.distance_mm === -1
            ? 'eau détectée'
            : item.distance_mm == null
              ? '—'
              : `${item.distance_mm} mm`}
        </strong>
      </div>
      <div style={{ fontSize: 12, color: 'var(--vrt-text-muted)', marginBottom: 10 }}>
        Dernière mesure : {timeAgo(item.last_update)}
      </div>

      {/* History chart */}
      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            color: 'var(--vrt-navy)',
            marginBottom: 6,
          }}
        >
          Historique
        </div>
        {loading ? (
          <div
            style={{
              padding: 18,
              textAlign: 'center',
              color: 'var(--vrt-text-muted)',
              background: 'var(--vrt-bg-alt)',
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            Chargement…
          </div>
        ) : err ? (
          <div
            style={{
              padding: 12,
              textAlign: 'center',
              color: '#a23123',
              background: '#fdecea',
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            {err}
          </div>
        ) : !chartData.length ? (
          <div
            style={{
              padding: 18,
              textAlign: 'center',
              color: 'var(--vrt-text-muted)',
              background: 'var(--vrt-bg-alt)',
              borderRadius: 6,
              fontSize: 12,
            }}
          >
            Aucune mesure disponible.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 4 }}>
              <defs>
                <linearGradient id="vrt-sensor-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2EB6A7" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2EB6A7" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--vrt-border)" />
              <XAxis
                dataKey="label"
                style={{ fontSize: 10 }}
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis domain={[0, 100]} style={{ fontSize: 10 }} width={28} />
              <Tooltip
                contentStyle={{
                  background: '#fff',
                  border: '1px solid var(--vrt-border)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  fontSize: 11,
                }}
                labelStyle={{ color: 'var(--vrt-navy)', fontWeight: 600 }}
                formatter={(value, name, props) => {
                  if (props?.payload?.is_water) return ['Eau détectée', 'Mesure'];
                  return [`${value}%`, 'Taux'];
                }}
              />
              <Area
                type="monotone"
                dataKey="taux"
                stroke="#1A2849"
                strokeWidth={2}
                fill="url(#vrt-sensor-area)"
                isAnimationActive={false}
                dot={false}
                activeDot={{ r: 4, fill: '#2EB6A7', stroke: '#fff', strokeWidth: 1.5 }}
              />
              {waterPoints.map((p) => (
                <ReferenceDot
                  key={p.idx}
                  x={p.label}
                  y={p.taux ?? 100}
                  r={4}
                  fill="#c0392b"
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <button
        type="button"
        onClick={onOpenDetail}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          width: '100%',
          padding: '8px 12px',
          borderRadius: 6,
          background: '#2EB6A7',
          color: '#fff',
          border: 'none',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ExternalLink size={12} /> Détail capteur
      </button>
    </div>
  );
}
